import { CustomCalendarType, SimpleActivity } from "@ac-expo/api-types";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { FileType } from "@prisma/client";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import { useCallback } from "react";
import { cn } from "../../../utils";
import { CaretLeftIcon, CaretRightIcon } from "../../icons";
import { FiltersBars } from "./filter-bars";
import { PlanningEventCard } from "./PlanningCards";
import { getRenderEventContent } from "./render-event";
import { usePlanning } from "./use-planning";
import {
  EventSchedulesTimes,
  formatActivityDays,
  formatActivityTime,
  formatWeekRange,
  getActivityBackgroundColor,
  getBackgroundContainerStyle,
  getBackgroundOverlayStyle,
  getUniqueActivities,
  isPlanningGroup,
  PlanningEvent,
} from "./utils";

interface MobilePlanningComponentProps {
  activities: SimpleActivity[];
  eventSchedules?: EventSchedulesTimes[];
  isRegisterEnabled?: boolean;
  onOpenActivitiesDetails?: (events: PlanningEvent[]) => void;
  customCalendar?: CustomCalendarType;
  getFileUrl?: (key?: string, type?: FileType, width?: number) => string;
  isShrinked?: boolean;
}

const MobilePlanningComponent = ({
  activities,
  isRegisterEnabled,
  eventSchedules,
  onOpenActivitiesDetails,
  customCalendar,
  getFileUrl,
  isShrinked,
}: MobilePlanningComponentProps) => {
  const {
    groupedEvents,
    groupedByWeek,
    minTime,
    maxTime,
    setDay,
    groupedPlaces,
    handlePrev,
    filteredSlots,
    handleNext,
    calendarRef,
    visibleDays,
    currentDay,
    setPlaceFilter,
    allDaysWithActivities,
    setVisibleDays,
    isFirstMonth,
    isLastMonth,
  } = usePlanning({
    activities,
    eventSchedules,
    customCalendarModel: customCalendar?.model,
  });

  const handleDatesSet = useCallback(() => {
    const days = allDaysWithActivities.map((d) => dayjs(d));
    setVisibleDays(days);
  }, [allDaysWithActivities, setVisibleDays]);

  const backgroundStyleOptions = {
    backgroundPictureUrl:
      customCalendar?.backgroundPicture && getFileUrl
        ? getFileUrl(customCalendar.backgroundPicture.key, "media")
        : undefined,
    backgroundColor: customCalendar?.backgroundColor,
    opacity: customCalendar?.opacity,
  };

  const placeNames = Object.keys(groupedPlaces).filter(
    (place) => place.length > 0
  );

  if (customCalendar?.model === "verticalWeekly") {
    return (
      <div className="h-[680px] overflow-scroll">
        <div className="gap-6 px-3 py-2">
          <div className="flex items-center justify-between">
            <FiltersBars
              slots={filteredSlots}
              onOpenActivitiesDetails={onOpenActivitiesDetails}
              placeNames={placeNames}
              setPlaceFilter={setPlaceFilter}
            />
          </div>
        </div>
        <div className="flex w-full flex-row items-center justify-center gap-2 p-2">
          <button type="button" onClick={handlePrev} disabled={isFirstMonth}>
            <CaretLeftIcon
              className="cursor-pointer text-xl"
              width={24}
              height={24}
              fill={isFirstMonth ? "#e4e4e4" : "#287BB9"}
            />
          </button>
          <p className="text-lg font-bold text-black">
            {currentDay.format("MMMM YYYY").toUpperCase()}
          </p>
          <button type="button" onClick={handleNext} disabled={isLastMonth}>
            <CaretRightIcon
              className="cursor-pointer text-xl"
              width={24}
              height={24}
              fill={isLastMonth ? "#e4e4e4" : "#287BB9"}
            />
          </button>
        </div>
        <div
          className="relative size-full"
          style={getBackgroundContainerStyle(backgroundStyleOptions)}
        >
          <div
            className="size-full overflow-hidden object-fill"
            style={getBackgroundOverlayStyle(backgroundStyleOptions)}
          />
          <div className="relative z-20 flex size-full flex-col gap-4 overflow-scroll p-4">
            {groupedByWeek.map((week, weekIndex) => {
              const uniqueActivities = getUniqueActivities(week.activities);
              return (
                <div key={weekIndex} className="flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <span className="whitespace-nowrap text-sm font-semibold capitalize text-gray-800">
                      {formatWeekRange(week.weekStart, week.weekEnd)}
                    </span>
                    <div className="h-px flex-1 bg-gray-300" />
                  </div>
                  <div className="flex flex-col gap-2">
                    {uniqueActivities.map((activity, actIndex) => {
                      const bgColor = getActivityBackgroundColor(
                        activity.allDay,
                        customCalendar?.fullDayColor,
                        activity.blockColor
                      );
                      const daysText = formatActivityDays(
                        activity,
                        week.activities
                      );
                      const timeText = formatActivityTime(activity);

                      return (
                        <button
                          type="button"
                          key={actIndex}
                          className="flex w-full cursor-pointer items-start justify-between rounded-lg p-3 text-left text-white"
                          style={{ backgroundColor: bgColor }}
                          onClick={() =>
                            onOpenActivitiesDetails?.([activity])
                          }
                        >
                          <div className="flex flex-col">
                            <span className="text-xs capitalize font-semibold text-gray-50">
                              {daysText} • {timeText}
                            </span>
                            <span className="text-xs font-bold text-gray-50">
                              {activity.title}
                            </span>
                          </div>
                          {isRegisterEnabled &&
                            activity.isRegistrationRequired &&
                            activity.hasAvailableTicketTypes !== false && (
                              <span className="rounded bg-white px-2 py-1 text-xs font-medium text-primary-500 border border-primary-400">
                                S&apos;inscrire
                              </span>
                            )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {groupedByWeek.length === 0 && (
              <div className="py-8 text-center text-gray-500">
                Aucune animation pour ce mois
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (customCalendar?.model === "verticalByPlace") {
    return (
      <div className="h-[680px] overflow-scroll">
        <div className="gap-6 px-3 py-2">
          <div className="flex items-center justify-between">
            <FiltersBars
              slots={filteredSlots}
              onOpenActivitiesDetails={onOpenActivitiesDetails}
              placeNames={placeNames}
              setPlaceFilter={setPlaceFilter}
            />
          </div>
        </div>
        <div
          className="relative size-full"
          style={getBackgroundContainerStyle(backgroundStyleOptions)}
        >
          <div
            className="size-full overflow-hidden object-fill"
            style={getBackgroundOverlayStyle(backgroundStyleOptions)}
          />
          <div className="relative z-20 flex size-full flex-col gap-4 overflow-scroll p-4">
            {Object.entries(groupedPlaces)
              .sort(([a], [b]) => {
                if (a === "") return 1;
                if (b === "") return -1;
                return a.localeCompare(b, "fr");
              })
              .map(([group, slots]) => (
              <div key={group || "no-place"} className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <p className="whitespace-nowrap text-base font-bold text-black">
                    {group.length > 0 ? group : "Sans lieu affecté"}
                  </p>
                  <div className="h-px flex-1 bg-gray-300" />
                </div>
                <div className="flex flex-col gap-2">
                  {slots
                    .sort(
                      (a, b) =>
                        new Date(a.start).getTime() -
                        new Date(b.start).getTime()
                    )
                    .map((slot, slotIndex) => (
                      <PlanningEventCard
                        key={slotIndex}
                        event={slot}
                        openDetails={onOpenActivitiesDetails}
                        withRegister={
                          isRegisterEnabled &&
                          !isPlanningGroup(slot) &&
                          slot.isRegistrationRequired
                        }
                        customCalendar={customCalendar}
                        isMobile={true}
                      />
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="size-full overflow-y-scroll">
      <div className="gap-6 px-3 py-2">
        <FiltersBars
          slots={filteredSlots}
          onOpenActivitiesDetails={onOpenActivitiesDetails}
          placeNames={placeNames}
          setPlaceFilter={setPlaceFilter}
        />
      </div>
      <div className="flex items-center justify-start border-r-2 pl-[14px] pr-[6px]">
        <p className="text-sm uppercase text-gray-500">
          {dayjs(currentDay).format("MMMM YYYY")}
        </p>
      </div>
      <div className="flex w-full flex-row gap-2 overflow-x-scroll pb-2">
        {visibleDays.map((day, index) => (
          <button
            type="button"
            key={index}
            className="flex-1 cursor-pointer px-2 text-center"
            onClick={() => setDay(day)}
          >
            <p className="text-xs font-bold capitalize">
              {day.format("dd")[0]}
            </p>
            <div
              className={cn("flex items-center justify-center rounded-lg py-1")}
            >
              <span
                className={cn(
                  "rounded-md px-2 py-1 text-xs font-bold text-gray-600",
                  {
                    "bg-secondary-500 text-white": currentDay.isSame(
                      day,
                      "day"
                    ),
                  }
                )}
              >
                {day.format("D")}
              </span>
            </div>
          </button>
        ))}
      </div>
      <div
        className="relative size-full overflow-hidden"
        style={{
          ...getBackgroundContainerStyle(backgroundStyleOptions),
          width: isShrinked ? "200%" : "100%",
        }}
      >
        <div
          className="size-full overflow-hidden object-fill"
          style={getBackgroundOverlayStyle(backgroundStyleOptions)}
        />
        <FullCalendar
          timeZone={"UTC"}
          ref={calendarRef}
          plugins={[
            timeGridPlugin,
            interactionPlugin,
            listPlugin,
            dayGridPlugin,
          ]}
          initialView="timeGridSevenDay"
          headerToolbar={false}
          events={
            customCalendar?.model === "verticalDayHour"
              ? groupedEvents
              : filteredSlots
          }
          views={{
            timeGridDay: {
              type: "timeGrid",
              duration: { days: 1 },
              buttonText: "day",
            },
            timeGridSevenDay: {
              type: "timeGrid",
              duration: { days: 1 },
              buttonText: "7 day",
            },
            listSevenDay: {
              type: "dayGrid",
              duration: { days: 1 },
              buttonText: "7 day",
            },
          }}
          datesSet={handleDatesSet}
          slotMinTime={minTime}
          slotMaxTime={maxTime}
          allDaySlot={true}
          slotLabelFormat={{
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }}
          slotDuration="01:00:00"
          expandRows={true}
          height={704}
          contentHeight={740}
          dayHeaders={false}
          eventClassNames="bg-transparent border-0"
          slotLabelContent={(args) => <div className="px-2">{args.text}</div>}
          eventContent={getRenderEventContent({
            onOpenActivitiesDetails,
            isRegisterEnabled,
            customCalendar,
          })}
          allDayText="Journée entière"
        />
      </div>
    </div>
  );
};

export { MobilePlanningComponent };
export default MobilePlanningComponent;
